import axios, { ResponseType } from 'axios';
import { APIResponse, CartItem, SerializedCartItem, ShopPayload, WebCategory, WebProduct, ShippingMethod, WebData, InvoiceForm, SitePage, OrderData, PublicCareer, UserCredit, UserCreditsBalance, UserLevelsData, UserOrder, CheckoutDeliveryResult, CheckoutPaymentResult, CheckoutResponseResult, CheckoutLinkResult, ClaimCreditsResult, UserLogin } from './ClassesPublic';
import { CheckoutOrder, Location } from '@cocoan/components/Classes';

const ROOT_URL = process.env.REACT_APP_API_URL
const DEBUG = process.env.REACT_APP_DEBUG==='1';
const IS_SANDBOX = process.env.REACT_APP_SANDBOX==='1';
const PAGE_AUTH = process.env.REACT_APP_PAGE_AUTH;

const PAYFRONT_CONFIG = {
	client_id: process.env.REACT_APP_PAYFRONT_ID,
	public_key: process.env.REACT_APP_PAYFRONT_KEY,
}

const OPENPAY_CONFIG = {
	merchant_id: process.env.REACT_APP_OPENPAY_ID,
	api_key: process.env.REACT_APP_OPENPAY_KEY,
}

var CACHE : { [x: string]: APIResponse<any> } = {};

interface PostOptions{
	responseType?: ResponseType,
	endpoint?: string,
}

var scrollTop = ()=>{
	window.scrollTo(0,0);
}

async function post<T=any>(endpoint: string, data?: any, opts?: PostOptions) : Promise<APIResponse<T>>{
	var options = {
		headers: {
			'token': '',
			'Content-Type': 'application/json'
		}
	}
	if(data instanceof FormData){
		options.headers['Content-Type'] = 'multipart/form-data';
	}

	try{
		var ENDPOINT = (opts && opts.endpoint) ? opts.endpoint : `${ROOT_URL}/public/${endpoint}`;
		if(DEBUG) console.log("POST "+ENDPOINT);
		var res = await axios.post(ENDPOINT, data, {
			...options,
			responseType: opts?.responseType || 'json'
		});
		if(DEBUG) console.log(` => Response: ${(parseInt(res.headers['content-length'])/1024).toFixed(2)} KB`);
		
		// Si es un blob, retornar directamente los datos
		if(opts?.responseType === 'blob'){
			return res.data as APIResponse<T>;
		}
		
		return res.data as APIResponse<T>;
	}catch(err){
		if(DEBUG) console.error(err);
		if(err && (err as any).response && (err as any).response.status===429){
			return {
				error: true,
				message: 'Se ha pasado la cantidad de intentos disponbiles.',
				code: 'API-RATE-429',
			}
		}
		return {
			error: true, 
			message: 'Hubo un error inesperado (API-LCL-1)',
			code: 'API-LCL-1',
		}
	}
};

var cachePost = async <T>(endpoint: string, key: string, data?: any) : Promise<APIResponse<T>>=>{
	if(CACHE[key]) return CACHE[key] as APIResponse<T>;
	var res = await post(endpoint, data);
	if(!res.error) CACHE[key] = res;
	return res;
}

var getAuthUrl = (path: string | null = null) => {
	if (!PAGE_AUTH) return null;
	var continue_url = `${window.location.origin}/auth`;
	var redirect_url = path 
	  ? `${window.location.href.replace(/\/$/, '')}/${path}` 
	  : window.location.href;
	return `${PAGE_AUTH}?continue=${encodeURIComponent(continue_url)}&redirect=${encodeURIComponent(redirect_url)}`;
 };

var getAuthFrameUrl = () => {
	if (!PAGE_AUTH) return null;
	var base = PAGE_AUTH.replace(/\/$/, '');
	return `${base}/frame`;
};


var getVariables = ()=>{
	return cachePost<WebData>('webdata', 'webdata');
}

var getAllCategories = ()=>{
	return cachePost<WebCategory[]>('categories', 'categories');
}

var getCategoryProducts = (category_id: number)=>{
	return post('categories/products', { category_id });
}

var getShopData = (categories: number[], query: string, offset: number=0, count: number=50)=>{
	return post<ShopPayload>('shop', { categories, query, offset, count })
}

var getProduct = (product_id: number, images: boolean=false, categories: boolean=false, metadata: boolean=false, related: boolean=false)=>{
	return post<WebProduct>('products/get', { product_id, images, related, categories, metadata });
}

var getCartItem = (product_id: number, quantity: number)=>{
	return post<CartItem>('cart/item', { product_id, quantity });
}

var getCart = (cart: SerializedCartItem[])=>{
	return post<CartItem[]>('cart/get', { cart });
}

var getShippingMethods = ()=>{
	return post<ShippingMethod[]>('checkout/methods/shipping');
}

var createOrder = (email: string, shipping_method: number, cart: SerializedCartItem[], newsletter: boolean=false, token: string=null)=>{
	return post<CheckoutOrder>('checkout/create', { email, shipping_method, cart, newsletter, token });
}

var getCheckoutOrder = (order_hash: string)=>{
	return post<CheckoutOrder>('checkout/get', { order_hash });
}

interface CheckoutShipping{
	shipping_method: number,
	owner_name: string,
	owner_last_name: string,
	owner_phone: string,
	shipping_date: number,
	gift: {
		gift_name: string,
		gift_last_name: string,
		gift_message: string,
		gift_phone: string,
	},
	location: Location
}
var sendCheckoutShipping = (order_hash: string, data: CheckoutShipping, token: string=null)=>{
	return post<CheckoutDeliveryResult>('checkout/delivery', { order_hash, token, ...data });
}

var getShippingClosedDates = (shipping_type: number)=>{
	return cachePost<number[]>('checkout/methods/days', `SHCLD-${shipping_type}`, { shipping_type });
}

var sendCardPayment = (order_hash: string, token: string, digits: string, bin: string)=>{
	return post<CheckoutPaymentResult>('checkout/payment/card', { order_hash, token, digits, bin });
}

var sendCardPaymentOpenpay = (order_hash: string, token: string, device: string, digits: string, bin: string)=>{
	return post<CheckoutPaymentResult>('checkout/payment/card/openpay', { order_hash, device, token, digits, bin });
}

var sendCreatePayment = (order_hash: string, method_id: number)=>{
	return post('checkout/payment/other', { order_hash, method_id });
}

var getOrderData = (order_hash: string)=>{
	return post('orders/get', { order_hash });
}

var getPaymentInstructions = (order_hash: string)=>{
	return post('orders/instructions', { order_hash });
}

var createOrderInvoice = (tax_data: InvoiceForm, location: Location)=>{
	return post('orders/invoice/create', { ...tax_data, location });
}

var searchOrder = (order_id: number, email: string)=>{
	return post('orders/search', { order_id , email });
}

var getCheckoutLink = (token: string)=>{
	return post<CheckoutLinkResult>('checkout/link', { token });
}

var getCheckoutResponse = (order_hash: string, payment_id: number)=>{
	return post<CheckoutResponseResult>('checkout/response', { order_hash, payment_id });
}

var getPage = (page_url: string)=>{
	return post<SitePage>('pages/get', {page_url}); 
}

var changePassword = (current_password: string, new_password: string, token: string)=>{
	return post<boolean>('password', { current_password, new_password, token });
}

var updateUser = (token: string, first_name: string, last_name: string, phone: string, birthday?: number, gender_id?: number)=>{
	return post('user/update', { token, first_name, last_name, phone, birthday, gender_id });
}

var getOrderByHash = (order_hash: string, token?: string)=>{
	return post<OrderData>('orders/hash', { order_hash, token });
}

var getUserOrders = (token: string, offset: number = 0, count: number = 20)=>{
	return post<{ orders: UserOrder[] }>('orders/list', { token, offset, count });
}

var getQuotationByHash = (order_hash: string)=>{
	return post<OrderData>('orders/hash', { order_hash });
}

var downloadQuotationPdf = async (order_hash: string, token?: string)=>{
	try{
		var res = await post('orders/pdf', { order_hash, token }, { responseType: 'blob' });
		var blob = new Blob([res as any], { type: 'application/pdf' });
		var url = window.URL.createObjectURL(blob);
		var a = document.createElement('a');
		a.style.display = 'none';
		a.href = url;
		a.download = `cotizacion_${order_hash.substring(0, 8)}.pdf`;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	}catch(err){
		throw err;
	}
};

var logout = ()=>{
	localStorage.removeItem('access_token');
	localStorage.removeItem('user_data');
	window.dispatchEvent(new Event('user'));
	window.dispatchEvent(new Event('token'));
}

async function getCareers(){
	return post<PublicCareer[]>('careers/list');
}

async function getCareer(career_id: number){
	return post<PublicCareer>('careers/get', { career_id });
}

var getUserCredits = (token: string, offset?: number, count?: number)=>{
	return post<UserCredit[]>('user/credits', { token, offset, count });
}

var getUserCreditsBalance = (token: string)=>{
	return post<UserCreditsBalance>('user/credits/balance', { token });
}

var getUserLevels = (token: string)=>{
	return post<UserLevelsData>('user/levels', { token });
}

var getAllLevels = (token: string)=>{
	return post<UserLevelsData>('user/levels/all', { token });
}

var claimOrderCredits = (token: string, order_hash: string)=>{
	return post<ClaimCreditsResult>('user/credits/claim', { token, order_hash });
}

var getUserWallet = (token: string)=>{
	return post<{ saveUrl: string, tier: string, tierLevel: number, points: number }>('user/wallet', { token });
}

var getGooglePass = (token: string)=>{
	return post<{ saveUrl: string, tier: string, tierLevel: number, points: number }>('user/wallet', { token, type: 'google' });
}

var getApplePass = (token: string)=>{
	return post<{ buffer: string, mimeType: string }>('user/wallet', { token, type: 'apple' });
}

var getUserInfo = (token: string)=>{
	return post<UserLogin>('user/info', { token });
}

var resendConfirmEmail = (token: string)=>{
	return post<boolean>('user/resend/confirm', { token });
}

export default {
	getUserInfo,
	IS_SANDBOX,
	OPENPAY_CONFIG,
	PAYFRONT_CONFIG,
	createOrder,
	createOrderInvoice,
	getAllCategories,
	getCart,
	getCartItem,
	getCategoryProducts,
	getCheckoutOrder,
	getCheckoutLink,
	getCheckoutResponse,
	getOrderData,
	getPage,
	getPaymentInstructions,
	getProduct,
	getUserOrders,
	getShippingClosedDates,
	getShippingMethods,
	getShopData,
	getVariables,
	scrollTop,
	searchOrder,
	sendCardPayment,
	sendCardPaymentOpenpay,
	sendCheckoutShipping,
	sendCreatePayment,
	logout,
	changePassword,
	updateUser,
	getOrderByHash,
	getQuotationByHash,
	downloadQuotationPdf,
	getCareers,
	getCareer,
	getAuthUrl,
	getAuthFrameUrl,
	getUserCredits,
	getUserCreditsBalance,
	getUserLevels,
	getAllLevels,
	getUserWallet,
	getGooglePass,
	getApplePass,
	claimOrderCredits,
	resendConfirmEmail,
}
